import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { publicEventSchema, type PublicEvent } from "./schemas";

export type PublicEventState =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "error"; error: string }
  | { status: "success"; event: PublicEvent };

export function usePublicEvent(slug: string): PublicEventState {
  const [state, setState] = useState<PublicEventState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const snap = await getDoc(doc(db, "public_events", slug));
        if (cancelled) return;

        if (!snap.exists()) {
          setState({ status: "not-found" });
          return;
        }

        const result = publicEventSchema.safeParse(snap.data());
        if (!result.success) {
          // Not "not found": the document exists and the link is valid.
          // Reporting a shape mismatch as missing disguised a real bug — a
          // null image made every image-less event look unpublished.
          setState({
            status: "error",
            error: "This event could not be displayed.",
          });
          return;
        }

        setState({ status: "success", event: result.data });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load event",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return state;
}
